import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Download, Plus, Trash2, Check, Loader2, Sparkles, Camera, Pencil, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function downloadTemplate() {
  const csv = "Name,Jersey,PTS,FGM,FGA,3PM,3PA,FTM,FTA,OREB,DREB,AST,STL,BLK,TO,FOUL\nJohn Smith,23,15,6,12,1,3,2,4,1,4,3,2,1,2,1\nJane Doe,11,8,3,8,0,2,2,2,0,3,5,1,0,1,2\n";
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'boxscore_template.csv';
  a.click();
}

const EMPTY_ROW = { name: '', jersey_number: '', pts: '', fgm: '', fga: '', three_m: '', three_a: '', ftm: '', fta: '', oreb: '', dreb: '', ast: '', stl: '', blk: '', to: '', foul: '' };

// Header synonym map mirrors backend `_BOXSCORE_HEADER_SYNONYMS`.
// Each canonical field maps to a list of normalized header strings.
const HEADER_SYNONYMS = {
  name: ['name', 'player', 'playername', 'fullname'],
  jersey_number: ['jerseynumber', 'jersey', 'jersey#', 'no', 'no.', 'number', 'num', '#'],
  pts: ['pts', 'points', 'p', 'tp', 'totalpoints'],
  fgm: ['fgm', 'madefg', 'fgmade', 'fg-m', 'fgmd'],
  fga: ['fga', 'fgatt', 'fg-a', 'fgatts', 'fgattempted'],
  three_m: ['3pm', '3pmade', '3ptm', '3fgm', 'threem', 'threepm', 'tpm', '3m'],
  three_a: ['3pa', '3patt', '3pta', '3fga', 'threea', 'threepa', 'tpa', '3a'],
  ftm: ['ftm', 'ftmade', 'ft-m', 'freethrowsmade'],
  fta: ['fta', 'ftatt', 'ft-a', 'freethrowsattempted', 'freethrowsa'],
  oreb: ['oreb', 'or', 'offreb', 'offensivereb', 'orb'],
  dreb: ['dreb', 'dr', 'defreb', 'defensivereb', 'drb'],
  ast: ['ast', 'assists', 'a', 'as'],
  stl: ['stl', 'steals', 's'],
  blk: ['blk', 'blocks', 'b', 'bs'],
  to: ['to', 'tov', 'turnovers', 'turnover'],
  foul: ['foul', 'fouls', 'pf', 'f', 'personalfouls', 'tf'],
};

const normHeader = (h) =>
  (h || '').toString().trim().toLowerCase().replace(/[^a-z0-9#]/g, '');

const mapCsvHeaders = (headerRow) => {
  if (!headerRow || headerRow.length === 0) return null;
  const mapping = {};
  const used = new Set();
  headerRow.forEach((raw, idx) => {
    const norm = normHeader(raw);
    if (!norm) return;
    for (const [field, syns] of Object.entries(HEADER_SYNONYMS)) {
      if (used.has(field)) continue;
      if (syns.includes(norm)) {
        mapping[idx] = field;
        used.add(field);
        break;
      }
    }
  });
  return used.has('name') ? mapping : null;
};

function BoxScoreEditor({ team, teamName, rows, setRows, gameId }) {
  const fileRef = useRef(null);
  const imageRef = useRef(null);
  const [scanning, setScanning] = useState(false);

  const updateRow = (idx, field, val) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  };

  const handleCSV = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result || '';
      // Auto-detect delimiter: tabs > commas > semicolons
      const sample = text.slice(0, 2048);
      const tabs = (sample.match(/\t/g) || []).length;
      const commas = (sample.match(/,/g) || []).length;
      const delim = tabs > commas ? '\t' : (sample.includes(';') && commas === 0 ? ';' : ',');
      const allRows = text
        .split(/\r?\n/)
        .map(l => l.trim())
        .filter(Boolean)
        .map(line => line.split(delim).map(s => s.trim().replace(/^"|"$/g, '')));

      const parsed = [];
      const headerMap = allRows.length > 0 ? mapCsvHeaders(allRows[0]) : null;

      const safeStr = (v) => (v === undefined || v === null ? '' : String(v));
      const blank = { name: '', jersey_number: '', pts: '0', fgm: '0', fga: '0', three_m: '0', three_a: '0', ftm: '0', fta: '0', oreb: '0', dreb: '0', ast: '0', stl: '0', blk: '0', to: '0', foul: '0' };

      if (headerMap) {
        // Header-mapped mode: skip first row, map by index
        for (let i = 1; i < allRows.length; i++) {
          const parts = allRows[i];
          const row = { ...blank };
          Object.entries(headerMap).forEach(([idx, field]) => {
            const val = parts[Number(idx)];
            if (val !== undefined) row[field] = safeStr(val);
          });
          if (!row.name) continue;
          parsed.push(row);
        }
      } else {
        // Legacy positional 16-column fallback
        for (let i = 0; i < allRows.length; i++) {
          const parts = allRows[i];
          if (i === 0 && (parts[0] || '').toLowerCase().includes('name')) continue;
          if (!parts[0]) continue;
          parsed.push({
            name: parts[0] || '', jersey_number: parts[1] || '',
            pts: parts[2] || '0', fgm: parts[3] || '0', fga: parts[4] || '0',
            three_m: parts[5] || '0', three_a: parts[6] || '0',
            ftm: parts[7] || '0', fta: parts[8] || '0',
            oreb: parts[9] || '0', dreb: parts[10] || '0',
            ast: parts[11] || '0', stl: parts[12] || '0',
            blk: parts[13] || '0', to: parts[14] || '0', foul: parts[15] || '0',
          });
        }
      }

      if (parsed.length > 0) {
        setRows(parsed);
        toast.success(`Imported ${parsed.length} player${parsed.length !== 1 ? 's' : ''}${headerMap ? ' (headers auto-mapped)' : ''}`);
      } else {
        toast.error('No valid rows found in CSV');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleScanImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PNG, JPG, WebP, or PDF file');
      return;
    }
    setScanning(true);
    const tId = toast.loading('AI is reading your box score…', { duration: 120000 });
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('team', team);
      formData.append('file', file);
      formData.append('preview', 'true');
      const res = await fetch(`${API_URL}/api/stats/games/${gameId}/scan-boxscore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        const text = await res.text().catch(() => '');
        toast.dismiss(tId);
        toast.error(`Server error (HTTP ${res.status})${text ? `: ${text.slice(0, 120)}` : ''}`);
        return;
      }
      toast.dismiss(tId);
      if (!res.ok) {
        toast.error(data.detail || `Scan failed (HTTP ${res.status})`);
        return;
      }
      // Populate ALL stat columns from the parsed rows so the user can review & edit
      const previewRows = (data.rows || []).map(p => ({
        name: p.name || '',
        jersey_number: p.jersey_number || '',
        pts: String(p.pts ?? 0),
        fgm: String(p.fgm ?? 0),
        fga: String(p.fga ?? 0),
        three_m: String(p.three_m ?? 0),
        three_a: String(p.three_a ?? 0),
        ftm: String(p.ftm ?? 0),
        fta: String(p.fta ?? 0),
        oreb: String(p.oreb ?? 0),
        dreb: String(p.dreb ?? 0),
        ast: String(p.ast ?? 0),
        stl: String(p.stl ?? 0),
        blk: String(p.blk ?? 0),
        to: String(p.to ?? 0),
        foul: String(p.foul ?? 0),
      }));
      if (previewRows.length === 0) {
        toast.error('No players detected — try a clearer photo');
        return;
      }
      setRows(previewRows);
      toast.success(`Scanned ${data.scanned_players} player${data.scanned_players === 1 ? '' : 's'}. Review the rows and click "Import ${teamName} Stats" to confirm.`);
    } catch (err) {
      toast.dismiss(tId);
      console.error('Scan image error:', err);
      toast.error(err?.message || 'Could not reach the server');
    } finally {
      setScanning(false);
    }
  };

  const COLS = [
    { key: 'name', label: 'Name', w: 'w-32 min-w-[128px]' },
    { key: 'jersey_number', label: '#', w: 'w-12' },
    { key: 'pts', label: 'PTS', w: 'w-12' },
    { key: 'fgm', label: 'FGM', w: 'w-12' },
    { key: 'fga', label: 'FGA', w: 'w-12' },
    { key: 'three_m', label: '3PM', w: 'w-12' },
    { key: 'three_a', label: '3PA', w: 'w-12' },
    { key: 'ftm', label: 'FTM', w: 'w-12' },
    { key: 'fta', label: 'FTA', w: 'w-12' },
    { key: 'oreb', label: 'OREB', w: 'w-12' },
    { key: 'dreb', label: 'DREB', w: 'w-12' },
    { key: 'ast', label: 'AST', w: 'w-12' },
    { key: 'stl', label: 'STL', w: 'w-12' },
    { key: 'blk', label: 'BLK', w: 'w-12' },
    { key: 'to', label: 'TO', w: 'w-12' },
    { key: 'foul', label: 'FLS', w: 'w-12' },
  ];

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-y-2">
          <h3 className="text-lg font-bold text-[#00247D] uppercase" style={{ fontFamily: 'Barlow Condensed' }}>{teamName || team.toUpperCase()}</h3>
          <div className="flex gap-1.5 flex-wrap">
            <input type="file" accept="image/*,application/pdf,.pdf" ref={imageRef} className="hidden" onChange={handleScanImage} />
            <Button
              size="sm"
              onClick={() => imageRef.current?.click()}
              disabled={scanning}
              className="text-xs h-7 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-black font-semibold border-amber-500"
              data-testid={`scan-image-${team}`}>
              {scanning ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Scanning...</>
              ) : (
                <><Sparkles className="w-3 h-3 mr-1" /> Scan from Image / PDF</>
              )}
            </Button>
            <input type="file" accept=".csv" ref={fileRef} className="hidden" onChange={handleCSV} />
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => fileRef.current?.click()} data-testid={`csv-import-${team}`}>
              <Upload className="w-3 h-3 mr-1" /> Import CSV
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={downloadTemplate} data-testid={`csv-template-${team}`}>
              <Download className="w-3 h-3 mr-1" /> Template
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b">
                {COLS.map(c => (
                  <th key={c.key} className="px-1 py-1 text-[9px] font-bold text-slate-500 uppercase text-center">{c.label}</th>
                ))}
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  {COLS.map(c => (
                    <td key={c.key} className="px-0.5 py-1">
                      <input
                        type={c.key === 'name' ? 'text' : c.key === 'jersey_number' ? 'text' : 'number'}
                        value={row[c.key]}
                        onChange={e => updateRow(idx, c.key, e.target.value)}
                        className={`${c.w} px-1.5 py-1 text-xs border border-slate-200 rounded text-center focus:outline-none focus:ring-1 focus:ring-[#00247D] ${c.key === 'name' ? 'text-left' : ''}`}
                        placeholder={c.key === 'name' ? 'Player name' : '0'}
                        data-testid={`box-${team}-${c.key}-${idx}`}
                      />
                    </td>
                  ))}
                  <td className="px-1">
                    {rows.length > 1 && (
                      <button onClick={() => setRows(prev => prev.filter((_, i) => i !== idx))} className="text-slate-300 hover:text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => setRows(prev => [...prev, { ...EMPTY_ROW }])} data-testid={`add-row-${team}`}>
          <Plus className="w-3 h-3 mr-1" /> Add Player
        </Button>
      </CardContent>
    </Card>
  );
}

export default function BoxScoreImportPage() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = new URLSearchParams(location.search).get('edit') === '1';
  const [teamARows, setTeamARows] = useState([{ ...EMPTY_ROW }]);
  const [teamBRows, setTeamBRows] = useState([{ ...EMPTY_ROW }]);
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState({ a: false, b: false });

  React.useEffect(() => {
    const token = localStorage.getItem('session_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    if (isEditMode) {
      // Fetch the boxscore to pre-fill rows from the current totals
      fetch(`${API_URL}/api/stats/games/${gameId}/boxscore`, { headers })
        .then(r => r.json())
        .then(data => {
          const g = data.game || data;
          setGame(g);
          const fromBox = (arr) => (arr || []).map(p => ({
            name: p.name || '',
            jersey_number: p.jersey || p.jersey_number || '',
            pts: String(p.pts ?? 0),
            fgm: String(p.fgm ?? 0),
            fga: String(p.fga ?? 0),
            three_m: String(p.three_m ?? 0),
            three_a: String(p.three_a ?? 0),
            ftm: String(p.ftm ?? 0),
            fta: String(p.fta ?? 0),
            oreb: String(p.oreb ?? 0),
            dreb: String(p.dreb ?? 0),
            ast: String(p.ast ?? 0),
            stl: String(p.stl ?? 0),
            blk: String(p.blk ?? 0),
            to: String(p.to ?? 0),
            foul: String(p.foul ?? 0),
          }));
          const aRows = fromBox(data.boxscore?.team_a);
          const bRows = fromBox(data.boxscore?.team_b);
          setTeamARows(aRows.length > 0 ? aRows : [{ ...EMPTY_ROW }]);
          setTeamBRows(bRows.length > 0 ? bRows : [{ ...EMPTY_ROW }]);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      fetch(`${API_URL}/api/stats/games/${gameId}`, { headers })
        .then(r => r.json()).then(setGame)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [gameId, isEditMode]);

  const submitBoxScore = async (team) => {
    const rows = team === 'a' ? teamARows : teamBRows;
    const validRows = rows.filter(r => r.name.trim());
    if (validRows.length === 0) { toast.error('Add at least one player'); return; }

    setImporting(true);
    try {
      const players = validRows.map(r => ({
        name: r.name,
        jersey_number: r.jersey_number,
        pts: parseInt(r.pts) || 0,
        fgm: parseInt(r.fgm) || 0,
        fga: parseInt(r.fga) || 0,
        three_m: parseInt(r.three_m) || 0,
        three_a: parseInt(r.three_a) || 0,
        ftm: parseInt(r.ftm) || 0,
        fta: parseInt(r.fta) || 0,
        oreb: parseInt(r.oreb) || 0,
        dreb: parseInt(r.dreb) || 0,
        ast: parseInt(r.ast) || 0,
        stl: parseInt(r.stl) || 0,
        blk: parseInt(r.blk) || 0,
        to: parseInt(r.to) || 0,
        foul: parseInt(r.foul) || 0,
      }));

      const token = localStorage.getItem('session_token');
      const url = isEditMode
        ? `${API_URL}/api/stats/games/${gameId}/boxscore-edit`
        : `${API_URL}/api/stats/games/${gameId}/import-boxscore`;
      const headers = { 'Content-Type': 'application/json' };
      if (isEditMode && token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify({ team, players })
      });
      if (res.ok) {
        const data = await res.json();
        if (isEditMode) {
          toast.success(`Stats updated for ${validRows.length} player${validRows.length !== 1 ? 's' : ''} (${data.entries_created} entries)`);
          // In edit mode, briefly show the "Saved" state then re-enable so the
          // user can keep iterating without a full page refresh.
          setImported(prev => ({ ...prev, [team]: true }));
          setTimeout(() => setImported(prev => ({ ...prev, [team]: false })), 1800);
        } else {
          toast.success(`Imported ${data.entries_created} stat entries for ${validRows.length} players`);
          setImported(prev => ({ ...prev, [team]: true }));
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || (isEditMode ? 'Save failed' : 'Import failed'));
      }
    } catch { toast.error('Network error'); }
    finally { setImporting(false); }
  };

  const finalizeGame = async () => {
    await fetch(`${API_URL}/api/stats/games/${gameId}/end`, { method: 'POST' });
    toast.success('Game finalized!');
    navigate(`/stats/summary/${gameId}`);
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" /></div>;
  if (!game) return <div className="p-8 text-center">Game not found</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(isEditMode ? `/stats/summary/${gameId}` : '/stats')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
              {isEditMode ? 'EDIT BOX SCORE' : 'IMPORT BOX SCORE'}
            </h1>
          </div>
          <div className="text-right">
            <div className="text-sm font-bold text-slate-700">{game.name}</div>
            <div className="text-xs text-slate-500">{game.team_a.name} vs {game.team_b.name}</div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {isEditMode ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-sm text-blue-900" data-testid="edit-mode-banner">
            <div className="flex items-start gap-2">
              <Pencil className="w-4 h-4 mt-0.5 shrink-0 text-blue-700" />
              <div>
                <p className="font-semibold mb-0.5">Editing box score</p>
                <p className="text-xs leading-relaxed">
                  {game?.entry_mode === 'post_game' ? (
                    <>Adjust each player's row and click <strong>Save</strong> to overwrite this team's stats. The current totals are pre-filled. Players removed from the table will be deleted from the team.</>
                  ) : (
                    <>This game was tracked live. Editing here <strong>preserves the play-by-play timeline</strong> — we only add or remove the exact stat events needed to match your edits. Quarter-by-quarter data, subs, and video timestamps stay intact.</>
                  )} Only the game owner can save changes.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-sm text-amber-800">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold mb-0.5">Three ways to import a box score:</p>
                <p className="text-xs leading-relaxed">
                  <strong>Scan from Image</strong> (AI) — snap a photo of any printed/digital scoresheet and our AI auto-fills it · <strong>Import CSV</strong> — paste from your tracking spreadsheet · <strong>Manual entry</strong> — type each row by hand. Players not yet on the roster will be added automatically.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          <div>
            <BoxScoreEditor team="a" teamName={game.team_a.name} rows={teamARows} setRows={setTeamARows} gameId={gameId} />
            <div className="mt-2 flex justify-end">
              <Button onClick={() => submitBoxScore('a')} disabled={importing || imported.a}
                className={imported.a ? 'bg-green-600 hover:bg-green-700' : 'bg-[#00247D] hover:bg-[#001A5C]'}
                data-testid="submit-boxscore-a">
                {imported.a ? <><Check className="w-4 h-4 mr-1" /> {isEditMode ? 'Saved' : 'Imported'}</> :
                 importing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {isEditMode ? 'Saving…' : 'Importing...'}</> :
                 isEditMode ? <><Save className="w-4 h-4 mr-1" /> Save {game.team_a.name} Stats</> :
                 `Import ${game.team_a.name} Stats`}
              </Button>
            </div>
          </div>

          <div>
            <BoxScoreEditor team="b" teamName={game.team_b.name} rows={teamBRows} setRows={setTeamBRows} gameId={gameId} />
            <div className="mt-2 flex justify-end">
              <Button onClick={() => submitBoxScore('b')} disabled={importing || imported.b}
                className={imported.b ? 'bg-green-600 hover:bg-green-700' : 'bg-[#00247D] hover:bg-[#001A5C]'}
                data-testid="submit-boxscore-b">
                {imported.b ? <><Check className="w-4 h-4 mr-1" /> {isEditMode ? 'Saved' : 'Imported'}</> :
                 importing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {isEditMode ? 'Saving…' : 'Importing...'}</> :
                 isEditMode ? <><Save className="w-4 h-4 mr-1" /> Save {game.team_b.name} Stats</> :
                 `Import ${game.team_b.name} Stats`}
              </Button>
            </div>
          </div>
        </div>

        {isEditMode && (imported.a || imported.b) && (
          <div className="mt-8 text-center">
            <Button size="lg" onClick={() => navigate(`/stats/summary/${gameId}`)}
              className="bg-[#00247D] hover:bg-[#001A5C] text-white h-12 px-8 text-base font-bold"
              data-testid="back-to-summary-btn">
              <ArrowLeft className="w-5 h-5 mr-2" /> Back to Game Summary
            </Button>
          </div>
        )}

        {!isEditMode && imported.a && imported.b && (
          <div className="mt-8 text-center">
            <Button size="lg" onClick={finalizeGame} className="bg-green-600 hover:bg-green-700 text-white h-14 px-10 text-base font-bold" data-testid="finalize-btn">
              <Check className="w-5 h-5 mr-2" /> Finalize Game & View Summary
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
